import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgImage from '../images/ranking.png';

const sortByMonth   = (g) => [...g].sort((a,b) => b.month !== a.month ? b.month - a.month : b.average !== a.average ? b.average - a.average : b.wines - a.wines);
const sortByAverage = (g) => [...g].sort((a,b) => b.average !== a.average ? b.average - a.average : b.wines !== a.wines ? b.wines - a.wines : b.month - a.month);
const sortByWines   = (g) => [...g].sort((a,b) => b.wines !== a.wines ? b.wines - a.wines : b.average !== a.average ? b.average - a.average : b.month - a.month);

function RkIcon({ name, size = 20 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.6, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'back':    return <svg {...p}><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
        case 'gangs':   return <svg {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
        case 'person':  return <svg {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
        case 'trophy':  return <svg {...p}><path d="M6 9V4h12v5a6 6 0 0 1-12 0Z"/><path d="M6 4H3v3a3 3 0 0 0 3 3M18 4h3v3a3 3 0 0 1-3 3"/><path d="M9 21h6M12 15v6"/></svg>;
        case 'chevron': return <svg {...p}><path d="m6 9 6 6 6-6"/></svg>;
        default: return null;
    }
}

function PodiumSlot({ gang, rank, sortMode }) {
    const { t } = useTranslation();
    if (!gang) return <div className="rk-podium-slot rk-podium-empty" />;
    const score = sortMode === 'monthly'
        ? gang.month
        : sortMode === 'general'
            ? parseFloat(gang.average || 0).toFixed(1) + '%'
            : gang.wines;

    return (
        <div className={`rk-podium-slot rk-p${rank}`}>
            <div className="rk-podium-medal">
                <span className="rk-podium-num">{rank}</span>
            </div>
            <div className="rk-podium-name">{gang.winegang_name}</div>
            <div className="rk-podium-gang">{t('ranking.members_count', { count: gang.members })}</div>
            <div className="rk-podium-score">{score}</div>
            <div className="rk-podium-base" />
        </div>
    );
}

export default function RankingGangs() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [gangsData, setGangsData] = useState([]);
    const [sortMode, setSortMode]   = useState('monthly');
    const [ready, setReady]         = useState(false);
    const [tooltip, setTooltip]     = useState(null);
    const [fetchError, setFetchError] = useState(false);
    const [retryKey, setRetryKey]     = useState(0);

    useEffect(() => {
        setFetchError(false);
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${process.env.REACT_APP_API_URL}/winegangs`, true);
        xhr.onreadystatechange = function () {
            if (this.readyState === 4) {
                if (this.status === 200) {
                    setGangsData(sortByMonth(JSON.parse(this.responseText)));
                    requestAnimationFrame(() => setReady(true));
                } else {
                    setFetchError(true);
                }
            }
        };
        xhr.send();
    }, [retryKey]);

    const sorted = sortMode === 'monthly'
        ? sortByMonth(gangsData)
        : sortMode === 'general'
            ? sortByAverage(gangsData)
            : sortByWines(gangsData);

    const tooltipText = {
        monthly: t('ranking.winegang_month_info'),
        general: t('ranking.winegang_general_info'),
        wines:   t('ranking.wines_info'),
    };

    const MEDAL = ['#d4a017', '#9baab5', '#c47b32'];

    return (
        <div className={`rk-stage${ready ? ' rk-ready' : ''}`}>
            <div className="rk-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="rk-vignette" />
            <div className="rk-grain" />

            {fetchError && (
                <div className="page-fetch-error">
                    <p>{t('common.fetch_error')}</p>
                    <button onClick={() => { setFetchError(false); setRetryKey(k => k + 1); }}>{t('common.retry')}</button>
                </div>
            )}

            {/* Header */}
            <div className="rk-header">
                <button className="rk-back-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}>
                    <RkIcon name="back" size={18} />
                </button>
                <div className="rk-title-wrap">
                    <RkIcon name="trophy" size={16} />
                    <h1 className="rk-title">{t('ranking.title')}</h1>
                </div>
                <div className="rk-mode-tabs">
                    <Link to="/ranking" className="rk-mode-tab">
                        <RkIcon name="person" size={14} />
                        <span>{t('common.individual')}</span>
                    </Link>
                    <Link to="/ranking-gangs" className="rk-mode-tab rk-mode-active">
                        <RkIcon name="gangs" size={14} />
                        <span>{t('common.winegangs')}</span>
                    </Link>
                </div>
            </div>

            {/* Podium */}
            <div className="rk-podium">
                <PodiumSlot gang={sorted[1]} rank={2} sortMode={sortMode} />
                <PodiumSlot gang={sorted[0]} rank={1} sortMode={sortMode} />
                <PodiumSlot gang={sorted[2]} rank={3} sortMode={sortMode} />
            </div>

            {/* Table panel */}
            <div className="rk-panel">
                {/* Sort bar */}
                <div className="rk-sort-bar">
                    {[
                        { key: 'monthly', label: t('common.month') },
                        { key: 'general', label: t('common.level') },
                        { key: 'wines',   label: t('common.wines') },
                    ].map(({ key, label }) => (
                        <button
                            key={key}
                            className={`rk-sort-btn${sortMode === key ? ' rk-sort-active' : ''}`}
                            onClick={() => setSortMode(key)}
                            onMouseEnter={() => setTooltip(key)}
                            onMouseLeave={() => setTooltip(null)}
                        >
                            {label}
                            {sortMode !== key && <RkIcon name="chevron" size={11} />}
                        </button>
                    ))}
                </div>
                {tooltip && (
                    <div className="rk-tooltip">{tooltipText[tooltip]}</div>
                )}

                {/* Table */}
                <div className="rk-table">
                    <div className="rk-thead">
                        <span>{t('common.position')}</span>
                        <span>{t('ranking.winegang_members')}</span>
                        <span className={sortMode === 'monthly' ? 'rk-col-active' : ''}>{t('common.month')}</span>
                        <span className={sortMode === 'general' ? 'rk-col-active' : ''}>{t('common.level')}</span>
                        <span className={sortMode === 'wines'   ? 'rk-col-active' : ''}>{t('common.wines')}</span>
                    </div>
                    <div className="rk-tbody">
                        {sorted.map((gang, i) => {
                            const isTop3 = i < 3;
                            return (
                                <button
                                    key={gang.id}
                                    className={`rk-row${isTop3 ? ' rk-row-top' : ''}`}
                                    style={{ animationDelay: `${i * 0.04}s` }}
                                    onClick={() => navigate(`/gangs/${gang.id}`)}
                                >
                                    <span className="rk-pos">
                                        {isTop3 && (
                                            <span className="rk-medal-dot" style={{ background: MEDAL[i] }} />
                                        )}
                                        {i + 1}
                                    </span>
                                    <span className="rk-name">
                                        <span className="rk-username">{gang.winegang_name}</span>
                                        <span className="rk-gang">{t('ranking.members_count', { count: gang.members })}</span>
                                    </span>
                                    <span className={`rk-val${sortMode === 'monthly' ? ' rk-val-active' : ''}`}>{gang.month}</span>
                                    <span className={`rk-val${sortMode === 'general' ? ' rk-val-active' : ''}`}>{parseFloat(gang.average || 0).toFixed(1)}%</span>
                                    <span className={`rk-val${sortMode === 'wines'   ? ' rk-val-active' : ''}`}>{gang.wines}</span>
                                </button>
                            );
                        })}
                        {sorted.length === 0 && (
                            <div className="rk-empty">{t('common.loading')}</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
